<template>
    <div class="block-editor get-location">
        <div class="get-location-wrapper">
            <div class="inputTitle">Message</div>
            <div class="text-editor-wrapper" key="message">
                <TiptapEditor
                    v-model="clone.message"
                    :show-attributes="true"
                    :max-character-count="64"
                    :show-emoji="false"
                    placeholder="Type message"
                    :rows="1"
                />
            </div>

            <div class="attribute-picker-section">
                <span class="sectionTitle"> Save Latitude</span>
                <a-popover v-model="latitudePickerVisible" trigger="click" :overlayClassName="'no-padding'">
                    <div slot="content">
                        <AttributePicker :default-selected="clone.setLatitude" :pick="onLatitudeAttributePicked" />
                    </div>
                    <div class="attribute-input">
                        <div class="attribute-input-text-wrapper">
                            <div class="attribute-input-placeholder" v-if="$_.isEmpty(clone.setLatitude)">
                                {{ $t("chatbotv2.chooseAttributeType") }}
                            </div>
                            <div class="single-attribute-style" v-else :class="{ active: latitudePickerVisible }">
                                <AttributeIconPreview :attribute="clone.setLatitude" />
                                <span>{{ clone.setLatitude.toLocaleLowerCase() }}</span>
                            </div>
                        </div>
                        <span class="material-icons" v-if="!$_.isEmpty(clone.setLatitude)"> expand_more </span>
                    </div>
                </a-popover>
            </div>
            <div class="attribute-picker-section">
                <span class="sectionTitle"> Save Longitude</span>
                <a-popover v-model="longitudePickerVisible" trigger="click" :overlayClassName="'no-padding'">
                    <div slot="content">
                        <AttributePicker :default-selected="clone.setLongitude" :pick="onLongitudeAttributePicked" />
                    </div>
                    <div class="attribute-input">
                        <div class="attribute-input-text-wrapper">
                            <div class="attribute-input-placeholder" v-if="$_.isEmpty(clone.setLongitude)">
                                {{ $t("chatbotv2.chooseAttributeType") }}
                            </div>
                            <div class="single-attribute-style" v-else :class="{ active: longitudePickerVisible }">
                                <AttributeIconPreview :attribute="clone.setLongitude" />
                                <span>{{ clone.setLongitude.toLocaleLowerCase() }}</span>
                            </div>
                        </div>
                        <span class="material-icons" v-if="!$_.isEmpty(clone.setLongitude)"> expand_more </span>
                    </div>
                </a-popover>
            </div>

            <div class="inputTitle">Initial Location</div>
            <div class="selects-wrapper" key="initial-location">
                <span>Default Selected Country</span>
                <a-select
                    :value="clone.initialCountry"
                    placeholder="Select Country"
                    :filter-option="filterOption"
                    option-filter-prop="children"
                    show-search
                    @change="handleCountryChange"
                >
                    <a-select-option v-for="country of countries" :key="country.iso2" :value="country.iso2">
                        {{ country.name }}
                    </a-select-option>
                </a-select>
                <span>Default Selected State</span>
                <a-select
                    :value="clone.initialState"
                    placeholder="Select State"
                    :filter-option="filterOption"
                    option-filter-prop="children"
                    show-search
                    @change="handleStateChange"
                >
                    <a-select-option v-for="state of states" :key="state.state_code" :value="state.state_code">
                        {{ state.name }}
                    </a-select-option>
                </a-select>
                <span>Default Selected City</span>
                <a-select
                    :value="clone.initialCity"
                    placeholder="Select City"
                    :filter-option="filterOption"
                    option-filter-prop="children"
                    show-search
                    @change="handleCityChange"
                >
                    <a-select-option v-for="city of cities" :key="city.id" :value="city.id">
                        {{ city.name }}
                    </a-select-option>
                </a-select>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import TiptapEditor from "../../common/TiptapEditor";
import _ from "lodash";
import AttributePicker from "../../pickers/AttributePicker";
import AttributeIconPreview from "../../common/AttributeIconPreview";
import { sendSocketMessage, socketMessageStream } from "../../../../../../../utils/socketStream";
import { mapState } from "vuex";

const log = log2("GetLocation");
export default {
    name: "GetLocation",
    components: { TiptapEditor, AttributePicker, AttributeIconPreview },
    props: {
        value: {
            type: Object,
            default: () => ({
                setLatitude: "latitude",
                setLongitude: "longitude",
                initialCountry: "TR",
                initialState: "34",
                initialCity: null,
                delay: 2,
                message: "Lütfen konumunuzu paylaşınız.",
            }),
        },
    },
    data() {
        return {
            clone: _.cloneDeep(this.value),
            latitudePickerVisible: false,
            longitudePickerVisible: false,
            countries: [],
            states: [],
            cities: [],
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
        }),
    },
    methods: {
        handleCountryChange(value) {
            log(`handleCountryChange ${value}`);
            this.clone.initialCountry = value;
            this.clone.initialState = null;
            this.clone.initialCity = null;
            this.states = [];
            this.getStates(value);
        },
        handleStateChange(value) {
            log(`handleStateChange ${value}`);
            this.clone.initialState = value;
            this.clone.initialCity = null;
            this.cities = [];
            this.getCities(this.clone.initialCountry, value);
        },
        handleCityChange(value) {
            log(`handleCityChange ${value}`);
            this.clone.initialCity = value;
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        getCountries() {
            log("getCountries");
            sendSocketMessage(
                "location get-countries",
                {
                    siteId: this.siteId,
                },
                (countries) => {
                    this.countries = countries;
                }
            );
        },
        getStates(country) {
            log("getStates", country);
            sendSocketMessage(
                "location find-states",
                {
                    siteId: this.siteId,
                    country,
                },
                (states) => {
                    this.states = states;
                }
            );
        },
        getCities(country, state) {
            log("getCities", { country, state });
            sendSocketMessage(
                "location find-cities",
                {
                    siteId: this.siteId,
                    country,
                    state,
                },
                (cities) => {
                    this.cities = cities;
                }
            );
        },

        onLatitudeAttributePicked(attribute) {
            log("onLatitudeAttributePicked", attribute);
            this.clone.setLatitude = attribute;
            this.latitudePickerVisible = false;
        },
        onLongitudeAttributePicked(attribute) {
            log("onLongitudeAttributePicked", attribute);
            this.clone.setLongitude = attribute;
            this.longitudePickerVisible = false;
        },
        listenSocket() {
            this.socketSub = socketMessageStream.subscribe((message) => {
                const { cmd, data } = message;
                if (cmd === "list_countries_response") {
                    const { countries } = data;
                    this.countries = countries;
                }
                if (cmd === "find_states_response") {
                    const { states } = data;
                    this.states = states;
                }
                if (cmd === "find_cities_response") {
                    const { cities } = data;
                    this.cities = cities;
                }
            });
        },
    },
    mounted() {
        log("mounted", this.value);
        this.getCountries();
        if (this.clone.initialCountry) this.getStates(this.clone.initialCountry);
        if (this.clone.initialCountry && this.clone.initialState)
            this.getCities(this.clone.initialCountry, this.clone.initialState);
        this.listenSocket();
    },
    beforeDestroy() {
        this.socketSub.unsubscribe();
    },
    watch: {
        clone: {
            deep: true,
            handler: function (next, old) {
                log("clone changed", next, old, next.initialCountry !== old.initialCountry);
                this.$emit("input", next);
            },
        },
        value(val) {
            if (_.isEqual(val, this.clone)) {
                return;
            }
            this.clone = _.cloneDeep(val);
        },
    },
};
</script>

<style lang="scss">
.block-editor.get-location {
    padding: 30px 30px 0;

    .active-connection {
        align-items: center;
        background: #fff;
        border-radius: 6px;
        display: flex;
        font-size: 14px;
        line-height: 18px;
        margin-bottom: 20px;
        overflow: hidden;
        padding: 20px;
        width: 100%;
        word-break: break-word;

        .logos {
            align-items: center;
            display: flex;
            flex-shrink: 0;
            margin-right: 15px;
            margin-top: 4px;

            .logo1 {
                background: #4a6586;
            }

            .logo2 {
                background: #d0292e;
            }

            .logo1,
            .logo2 {
                height: 30px;
                padding: 4px;
                width: 30px;
            }
        }
    }

    .active-connection-text {
        flex-grow: 1;
        flex-shrink: 0;
        width: 0;

        div {
            overflow: hidden !important;
            text-overflow: ellipsis;
            white-space: pre-wrap;
        }
    }

    .get-location-wrapper {
        .inputTitle {
            margin-bottom: 10px;
            margin-top: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .editable-editor {
            p {
                margin-bottom: 0;
            }
        }
    }

    .attribute-picker-section {
        margin-top: 20px;

        .sectionTitle {
            margin-bottom: 10px;
            margin-top: 15px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .attribute-input {
            align-items: center;
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            height: 43px;
            padding: 15px;
            position: relative;
            transition: all 0.3s;
            user-select: none;
            width: 100%;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .attribute-input-text-wrapper {
                display: flex;
                flex-grow: 1;
                width: 0;
            }

            .attribute-input-placeholder {
                color: #9ba6b3;
                font-size: 15px;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .single-attribute-style {
                background: #ffdc66;
                cursor: pointer;
                transition: all 0.3s;
                border-radius: 10px;
                box-decoration-break: clone;
                padding: 0 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: 800;

                &.active {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #647283;
                    font-size: 18px;
                    margin-right: 3px;
                }
            }
        }
    }

    .selects-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
}
</style>
